import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Imobiliario } from "../../../interfaces";
import { DashboardContext } from "../../../providers/Dashboard";
import { getAll } from "../../../services/db";
import { unirImoveis } from "../../../services/api";
import { getSituacaoIptu } from "../../../services/api";
import toast from "react-hot-toast";

interface Props {
  municipio: string;
  openDialog: boolean;
  handleCloseDialog: () => void;
  fetchAndUpdateDashboardData: (id?: string, parameter?: string) => Promise<void>;
}

export function DialogJoin({ municipio, handleCloseDialog, openDialog, fetchAndUpdateDashboardData}: Props) {
  const { metadata, setMetadata } = useContext(DashboardContext);
  const [imoveis, setImoveis] = useState<Imobiliario[]>([]);
  const [selectedImovel, setSelectedImovel] = useState<Imobiliario | null>(null);
  const [imoveisIptu, setImoveisIptu] = useState<Record<number, string>>({});
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [openMainDialog, setOpenMainDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const situacoesIptuPermitidas = ["Não consta", "Imune", "Isento", "Cancelado", "Pago"];

  function handleCloseDialogs() {
    if (isProcessing) return;
    setOpenWarningDialog(false);
    setOpenMainDialog(false);
    handleCloseDialog();
  }

  async function fetchSituacaoIptu(idImobiliario: number) {
    try {
      if (municipio === "vitoriadaconquista") {
        return null;
      }
      const data = await getSituacaoIptu({
        municipio: municipio,
        id_imobiliario: [idImobiliario],
      });
      return data.length ? data[0].situacao_iptu : null;
    } catch (error) {
      toast.error("Erro ao buscar a situação do IPTU.");
      return null;
    }
  }

  useEffect(() => {
    async function verificarIptuPrincipal() {
      if (openDialog) {
        let situacao = null;
        if (municipio !== "maracanau") {
          situacao = await fetchSituacaoIptu(Number(metadata.id_imobiliario));
        }
        if (situacao && !situacoesIptuPermitidas.includes(situacao)) {
          setOpenWarningDialog(true);
        } else {
          setOpenMainDialog(true);
        }
      }
    }
    verificarIptuPrincipal();
  }, [openDialog, metadata.id_imobiliario]);


  useEffect(() => {
    async function loadImoveis() {
      const imoveisList = getAll("imobiliario")?.filter(
        (imovel: Imobiliario) => imovel.id !== Number(metadata?.id_imobiliario)
      ) as Imobiliario[];
  
      setImoveis(imoveisList);
  
      const iptuStatus: Record<number, string> = {};
      for (const imovel of imoveisList) {
        const situacao = await fetchSituacaoIptu(imovel.id);
        if (situacao) {
          iptuStatus[imovel.id] = situacao;
        }
      }
      setImoveisIptu(iptuStatus);
    }
  
    if (openDialog) {
      loadImoveis();
    }
  }, [metadata, openDialog]);  

  function handleSelectImovel(imovel: Imobiliario) {
    const situacaoImovel = imoveisIptu[imovel.id] || null;
    
    if (!situacoesIptuPermitidas.includes(situacaoImovel) && situacaoImovel !== null) {
      return;
    }
  
    setSelectedImovel((prev) => (prev?.id === imovel.id ? null : imovel));
  }
  

  async function handleJoin() {
    if (selectedImovel) {
      try {
        setIsProcessing(true);
        const resultado = await unirImoveis(metadata.id_imobiliario, Number(selectedImovel.id));
        if (resultado === "success") {
          setSelectedImovel(null);
          setIsProcessing(false);
          handleCloseDialogs();

          await fetchAndUpdateDashboardData(String(selectedImovel.id), `Join${municipio.charAt(0).toUpperCase() + municipio.slice(1)}`);

          setMetadata((current) => {
            return { ...current };
          });

          toast.success("União realizada com sucesso!");
        } else {
          setIsProcessing(false);
          toast.error("Falha na união.");
        }
      } catch (error) {
        console.error("Erro ao realizar a união:", error);
        toast.error("Ocorreu um erro ao tentar unir os imóveis.");
        setIsProcessing(false);
      }
    } else {
      console.log("Nenhum imóvel selecionado.");
    }
  }

  return (
    <>
      <Dialog
        open={openWarningDialog}
        disableEscapeKeyDown={isProcessing}
        onClose={(_, reason) => {
          if (isProcessing) return;
          handleCloseDialogs();
        }}
      >
        <DialogContent>
          <Typography variant="h5">Aviso</Typography>
          <Typography variant="body1" style={{ textAlign: "center", marginTop: "10px" }}>
            O imóvel possui dívida de IPTU em aberto e não pode ser unificado.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="success" variant="contained" disabled={isProcessing}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMainDialog}
        disableEscapeKeyDown={isProcessing}
        onClose={(_, reason) => {
          if (isProcessing) return;
          handleCloseDialogs();
        }}
      >
        <DialogContent>
          <h2 style={{ textAlign: "center" }}>Selecione uma unidade imobiliária para unir ao imóvel</h2>

          <List component="nav">
          {imoveis?.map((imovel: Imobiliario) => {
            const situacao = imoveisIptu[imovel.id] || null;
            const iptuPendencia = situacao !== null && !situacoesIptuPermitidas.includes(situacao);

            return (
              <ListItemButton
                key={imovel.id}
                sx={{
                  borderBottom: "1px solid #5f5f5f",
                  backgroundColor: selectedImovel?.id === imovel.id ? "#4b4b4b" : "inherit",
                  opacity: iptuPendencia ? 0.5 : 1,
                  cursor: iptuPendencia ? "not-allowed" : "pointer"
                }}
                onClick={() => !iptuPendencia && handleSelectImovel(imovel)}
              >
                <ListItemText
                  primary={imovel.inscricao_cartografica}
                  secondary={
                    iptuPendencia
                      ? `IPTU em aberto. Não pode ser unificado. Número da unidade imobiliária: ${imovel.nr_unidade} | Número de Cadastro: ${
                          imovel.numero_cadastro || "Não informado"
                        }`
                      : `Número da unidade imobiliária: ${imovel.nr_unidade} | Número de Cadastro: ${
                          imovel.numero_cadastro || "Não informado"
                        }`
                  }
                />
              </ListItemButton>
            );
          })}
          </List>
        </DialogContent>

        <DialogActions
          style={{
            display: "flex",
            justifyContent: "center"
          }}
        >
          <Button
            color="success"
            onClick={handleJoin}
            variant="contained"
            disabled={!selectedImovel || isProcessing}
            startIcon={isProcessing ? <CircularProgress color="inherit" size={20} /> : undefined}
          >
            {isProcessing ? "Realizando unificação..." : "Unificar imóveis"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
